import { ChangeEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z, ZodIssue } from "zod";
import $api from "@/zapi-frontend/shared/api/axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { Input, Button } from "@libs/zapi-ui/shadcn";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { CheckIcon, WarningIcon, LogoSvg } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";

const PasswordSchema = z.string().min(8).max(32).regex(/[A-Z]/).regex(/[0-9]/);
const CredentialsSchema = z.object({
  password: PasswordSchema,
  password2: PasswordSchema
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export function ResetPasswordPage() {
  const { navigate } = useHistoricalNavigate();
  const [credentials, setCredentials] = useState<TCredentials>({
    password: "",
    password2: ""
  });
  const [isErrorShown, setIsErrorShown] = useState(false);
  const [issues, setIssues] = useState<ZodIssue[]>([]);

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const { search } = useLocation();

  const resetPasswordToken = new URLSearchParams(search).get("key");

  const isPasswordLengthValid = credentials.password.length >= 8;
  const isPasswordHasOneNumber = credentials.password.match(/[0-9]/) !== null;
  const isPasswordHasOneCapitalLetter = credentials.password.match(/[A-Z]/) !== null;
  const isPasswordMatching = credentials.password && credentials.password === credentials.password2;

  const isValidCredentials = CredentialsSchema.safeParse(credentials).success && isPasswordMatching;

  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = {
        password: credentials.password,
        key: resetPasswordToken
      };
      return await $api.post("/auth/passwords/reset", payload);
    },
    onSuccess: () => {
      toast.info("Password reset successfully");
      navigate(getRouteProjects());
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to reset password";
      toast.error(message);
      setIsErrorShown(true);
    }
  });

  async function resetPassword() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
    } catch {
      return;
    }
  }

  function onChangeInput(e: ChangeEvent<HTMLInputElement>, name: string) {
    setIsErrorShown(false);
    setCredentials((prev) => ({ ...prev, [name]: e.target.value }));
  }

  function getOnChangeInput(name: string) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      onChangeInput(e, name);
    };
  }

  function calculateIssues() {
    const issues = CredentialsSchema.safeParse(credentials).error?.issues;
    setIssues(issues || []);
  }

  function getIssueMsg(name: string) {
    return issues.find((issue) => issue.path[0] === name)?.message;
  }

  function fInput({
    name,
    keyName,
    showErrors,
    type = "text",
    setShowPassword: setShowPasswordProp,
    showPassword: showPasswordProp,
    disabled
  }: {
    type?: "password" | "text";
    name: string;
    keyName: keyof TCredentials;
    showErrors?: boolean;
    showPassword?: boolean;
    setShowPassword?: (show: boolean) => void;
    disabled?: boolean;
  }) {
    return (
      <div className={"flex flex-col gap-[4px]"}>
        <label className={"text-inLabel font-medium mb-[4px]"}>{name}</label>

        <div className={"relative"}>
          <Input
            type={setShowPasswordProp ? (showPasswordProp ? "text" : "password") : type}
            value={credentials[keyName] ?? ""}
            onChange={getOnChangeInput(keyName)}
            onBlur={calculateIssues}
            required
            disabled={disabled}
          />
          {setShowPasswordProp && (
            <button
              type="button"
              onClick={() => setShowPasswordProp(!showPasswordProp)}
              aria-label={showPasswordProp ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm font-medium"
            >
              {showPasswordProp ? "Hide" : "Show"}
            </button>
          )}
        </div>
        <p className={"text-errorRed text-xs flex items-center gap-1"}>
          {showErrors && getIssueMsg(keyName) && <WarningIcon width={18} height={18} />}
          {showErrors && getIssueMsg(keyName)}
        </p>
      </div>
    );
  }

  return (
    <div className={"flex flex-col items-center h-[calc(100vh - 40px)]"}>
      <LogoSvg className={"my-[64px]"} />
      <div className={"max-w-[400px] w-full"}>
        <div>
          <div className={"flex mb-10 flex-col"}>
            <h5 className={"text-[32px]"}>Create a new password</h5>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              resetPassword();
            }}
          >
            <div className={"flex flex-col gap-[16px] mb-6"}>
              {fInput({
                name: "Password",
                keyName: "password",
                showErrors: false,
                type: "password",
                showPassword: showPassword,
                setShowPassword: setShowPassword
              })}

              {fInput({
                name: "Confirm Password",
                keyName: "password2",
                showErrors: false,
                type: "password",
                showPassword: showPassword2,
                setShowPassword: setShowPassword2
              })}

              <div className={"flex gap-[8px] flex-col"}>
                <div className={"flex gap-[8px] items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordLengthValid ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordLengthValid && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordLengthValid ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least 8 characters
                  </p>
                </div>

                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordHasOneNumber ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordHasOneNumber && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordHasOneNumber ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least one number
                  </p>
                </div>
                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordHasOneCapitalLetter ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordHasOneCapitalLetter && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordHasOneCapitalLetter ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least one capital letter
                  </p>
                </div>

                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordMatching ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordMatching && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordMatching ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    Passwords match
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="innoveraPrimary"
              className={"w-full"}
              disabled={!isValidCredentials || mutation.isPending}
              type="submit"
            >
              Reset Password
            </Button>

            <p className={"text-errorRed text-xs pt-4"}>{isErrorShown && "Failed to reset password"}</p>
          </form>
        </div>
      </div>
    </div>
  );
}
